import { FC } from 'react';
import { Loader } from '@new/common/ui/Loader/Loader';
import { useCourseStats } from '../../hooks/useCourseStats';
import { formatMinutesToHHMM } from '../../lib/courseStats';

interface CourseDataStatsProps {
  courseId: number;
}

/**
 * Instructor-facing "Course Data" panel. Shows aggregate counts and time
 * totals used to estimate course contact hours (e.g. for accreditation).
 */
export const CourseDataStats: FC<CourseDataStatsProps> = ({ courseId }) => {
  const { stats, isLoading } = useCourseStats(courseId);

  if (isLoading || !stats) {
    return (
      <div className="rounded-xl bg-(--neutral-white-NW1000) p-4 dark:bg-gray-900">
        <Loader showLogo={false} />
      </div>
    );
  }

  const statItems: { name: string; value: string | number }[] = [
    { name: 'Number of objectives', value: stats.numberOfObjectives },
    { name: 'Number of assessments', value: stats.numberOfAssessments },
    { name: 'Number of media', value: stats.numberOfMedia },
    { name: 'Objectives with assessments', value: `${stats.percentObjectivesWithAssessments}%` },
    { name: 'Objectives with media', value: `${stats.percentObjectivesWithMedia}%` },
    { name: 'Total media time (hh:mm)', value: formatMinutesToHHMM(stats.totalMediaTimeMinutes) },
    {
      name: 'Total exercise time (hh:mm)',
      value: formatMinutesToHHMM(stats.totalExerciseTimeMinutes),
    },
    { name: 'Total course time (hh:mm)', value: formatMinutesToHHMM(stats.totalCourseTimeMinutes) },
  ];

  return (
    <dl className="mx-auto grid grid-cols-1 gap-3 overflow-hidden rounded-xl sm:grid-cols-2 lg:grid-cols-4">
      {statItems.map((stat) => (
        <div
          key={stat.name}
          className="flex flex-wrap items-baseline justify-between gap-2 border border-(--neutral-black-NB200) shadow-sm rounded-xl bg-(--neutral-white-NW1000) p-4 dark:bg-gray-900"
        >
          <dt className="[font-size:var(--fs-l5)] md:[font-size:var(--fs-l4)] font-medium text-gray-500 dark:text-gray-400 m-0!">
            {stat.name}
          </dt>
          <dd className="w-full flex-none [font-size:var(--fs-h6)] md:[font-size:var(--fs-h4)] lg:[font-size:var(--fs-h2)] font-medium tracking-tight text-gray-900 dark:text-(--neutral-white-NW1000) m-0!">
            {stat.value}
          </dd>
        </div>
      ))}
    </dl>
  );
};
